import { useEffect, useState, useRef } from "react";
import GitFitGridLoader from "@/components/GitFitGridLoader";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase, getOAuthRedirectUrl, getPasswordResetRedirectUrl } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useAppSession } from "@/providers/AppSessionProvider";
import { safeNavigate } from "@/lib/navigationGuard";
import { useTheme } from "@/providers/ThemeProvider";
import { useQueryClient } from "@tanstack/react-query";
import { captureException, addBreadcrumb, setContext } from "@/lib/sentry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, ShieldCheck, Eye, EyeOff } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { motion, AnimatePresence } from "framer-motion";
import { AuthLanding } from "@/components/auth/AuthLanding";
import { detectNativeApp, openInExternalBrowser, isIOS, isAndroid, isAppleSignInAvailable, isNativeApp } from "@/lib/platform";
import { useHoneypot } from "@/hooks/useHoneypot";
import { FAKE_AUTH_SUCCESS, getFakeBotResponse } from "@/lib/fakeBotResponses";
import { loadPreAuthMetrics, savePreAuthMetricsToBackend } from "@/lib/preAuthMetrics";
import { signInWithApple } from "@/lib/appleSignIn";
import { OnboardingFlow } from "@/components/auth/OnboardingFlow";
import { SafeHCaptcha } from "@/components/SafeHCaptcha";
import { isDisposableEmail, getDisposableEmailErrorMessage } from "@/lib/disposableEmails";
import { trackOnboardingEvent, trackStoreClick } from "@/lib/onboardingAnalytics";
import { getStoredLandingVariant } from "@/lib/abTest";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const HCAPTCHA_SITE_KEY = "eebe1a6c-3fc4-42f4-9df4-6bee9822d538";

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [showAppleSignIn, setShowAppleSignIn] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [showGoogleConfirm, setShowGoogleConfirm] = useState(false);

  // Username/password state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordEmail, setPasswordEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showSignUp, setShowSignUp] = useState(searchParams.get("signup") === "true");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showEmailSection, setShowEmailSection] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageCheckboxGlow, setAgeCheckboxGlow] = useState(false);

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Honeypot for bot detection
  const { isBot: isSignInBot, honeypotProps: signInHoneypotProps } = useHoneypot("website_signin");
  const { isBot: isSignUpBot, honeypotProps: signUpHoneypotProps } = useHoneypot("website_signup");

  // Detect mobile web (not native app) to show app store buttons
  const isMobileWeb = typeof navigator !== 'undefined' && (isIOS() || isAndroid()) && !isNativeApp();

  // Android keyboard fix - track if any input is focused
  const [isInputFocused, setIsInputFocused] = useState(false);
  const isAndroidDevice = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

  // Android keyboard dismiss detection via visualViewport
  // On Android, dismissing the keyboard (e.g. via back button) may not fire blur,
  // so we watch for the viewport expanding back to reset the transform.
  useEffect(() => {
    if (!isAndroidDevice) return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const fullHeight = viewport.height;

    const handleResize = () => {
      // Keyboard is closed when viewport returns to near full height
      if (viewport.height >= fullHeight * 0.75) {
        setIsInputFocused(false);
      }
    };

    viewport.addEventListener('resize', handleResize);
    return () => viewport.removeEventListener('resize', handleResize);
  }, [isAndroidDevice]);

  // Check for password recovery mode FIRST before setting initial view
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  // Skip password reset detection entirely if we're on the oauth-redirect page
  const isOnOAuthRedirectPage = window.location.pathname === '/oauth-redirect';

  const isRecoveryMode = urlParams.get("type") === "recovery" || hashParams.get("type") === "recovery";
  // OAuth callback can come from custom URL scheme or web
  const isOAuthCallback = isOnOAuthRedirectPage || urlParams.get("source") === "oauth" || window.location.pathname === '/auth/callback';

  // Implicit flow uses tokens in hash (access_token, refresh_token)
  // PKCE flow uses 'code' parameter for password reset links
  // GitHub OAuth also uses 'code' but includes a 'state' parameter
  const hasAccessToken = hashParams.has("access_token");
  const hasAuthCode = urlParams.has("code");
  const hasGitHubState = urlParams.has("state");

  // Consider it a password reset if:
  // 1. We're NOT on the OAuth redirect page, AND
  // 2. (type=recovery is present, OR
  //     We have access_token in hash (implicit flow) AND it's NOT an OAuth callback, OR
  //     We have a code but no GitHub state (PKCE flow from email))
  const isPasswordReset = !isOnOAuthRedirectPage && (isRecoveryMode || (hasAccessToken && !isOAuthCallback) || (hasAuthCode && !hasGitHubState && !isOAuthCallback));

  if (isRecoveryMode) {
    console.log("[Auth] Password recovery mode detected in URL (type=recovery)");
  }
  if (hasAuthCode || hasAccessToken) {
    console.log("[Auth] Auth code/token detected in URL", {
      pathname: window.location.pathname,
      isOnOAuthRedirectPage,
      isOAuthCallback,
      code: urlParams.get("code"),
      type: urlParams.get("type"),
      source: urlParams.get("source"),
      hasState: hasGitHubState,
      hasAccessToken,
      isPasswordReset,
      hash: window.location.hash.substring(0, 50),
      fullURL: window.location.href.substring(0, 100)
    });
  }

  // Check if we should show signup directly (e.g., from onboarding)
  const shouldShowSignup = searchParams.get("signup") === "true";
  const fromOnboarding = searchParams.get("from") === "onboarding" || searchParams.get("from") === "mobile" || searchParams.get("from") === "tour";

  const [view, setView] = useState<"landing" | "signin" | "reset-password">(
    isPasswordReset ? "reset-password" : shouldShowSignup ? "signin" : "landing"
  );
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isInOnboarding, setIsInOnboarding] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showPasswordResetError, setShowPasswordResetError] = useState(false);

  // hCaptcha state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaInitiated, setCaptchaInitiated] = useState(false);
  const captchaRef = useRef<any>(null);
  const captchaTokenRef = useRef<string | null>(null); // Ref for immediate access

  // Pre-load hCaptcha when on signin view to reduce wait time
  useEffect(() => {
    if (view === "signin" && !captchaInitiated) {
      console.log('[Auth] Pre-loading hCaptcha on signin view mount');
      // Reset token state to ensure fresh captcha
      captchaTokenRef.current = null;
      setCaptchaToken(null);
      setCaptchaInitiated(true);
    }
  }, [view, captchaInitiated]);

  // Reset captcha state when coming from onboarding
  useEffect(() => {
    if (fromOnboarding && view === "signin") {
      console.log('[Auth] Coming from onboarding, resetting captcha token');
      captchaTokenRef.current = null;
      setCaptchaToken(null);
      // Don't auto-execute here - let it execute when user initiates sign-in
      // This prevents race conditions with hCaptcha's internal initialization
    }
  }, [fromOnboarding, view]);

  const handleCaptchaLoad = () => {
    console.log('[Auth] hCaptcha loaded');
    console.log('[Auth] hCaptcha ref state:', {
      hasRef: !!captchaRef.current,
      refType: typeof captchaRef.current,
      hasExecute: typeof captchaRef.current?.execute,
      hasReset: typeof captchaRef.current?.resetCaptcha,
    });

    if (!captchaRef.current) {
      console.error('[Auth] No captchaRef.current available on load');
      return;
    }

    // Store ref in closure to ensure it's available
    const captchaInstance = captchaRef.current;

    // Verify methods exist before attempting async execution
    const hasRequiredMethods =
      typeof captchaInstance.execute === 'function' &&
      typeof captchaInstance.resetCaptcha === 'function';

    if (!hasRequiredMethods) {
      console.warn('[Auth] Captcha instance missing required methods, waiting for full initialization');
      return;
    }

    // Don't auto-execute on load - only execute when user actually tries to sign in
    // This prevents race conditions with hCaptcha's internal initialization
    console.log('[Auth] hCaptcha ready for use');
  };

  const handleCaptchaVerify = (token: string) => {
    console.log('[Auth] hCaptcha verified, token received');
    captchaTokenRef.current = token; // Set ref immediately
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    console.log('[Auth] hCaptcha token expired, will re-execute');
    captchaTokenRef.current = null;
    setCaptchaToken(null);
    // Auto re-execute so the captcha is always ready
    setTimeout(() => {
      if (captchaRef.current && typeof captchaRef.current.execute === 'function') {
        try {
          console.log('[Auth] Re-executing captcha after expiry');
          captchaRef.current.execute();
        } catch (e) {
          console.error('[Auth] Failed to re-execute captcha after expiry:', e);
        }
      }
    }, 1000);
  };

  const handleCaptchaError = (err: string) => {
    console.error('[Auth] hCaptcha error:', err);
    captchaTokenRef.current = null;
    setCaptchaToken(null);
    // Auto-retry after a short delay — captcha must not be skippable
    setTimeout(() => {
      if (captchaRef.current) {
        try {
          console.log('[Auth] Re-executing captcha after error');
          if (typeof captchaRef.current.resetCaptcha === 'function') {
            captchaRef.current.resetCaptcha();
          }
          if (typeof captchaRef.current.execute === 'function') {
            captchaRef.current.execute();
          }
        } catch (e) {
          console.error('[Auth] Failed to re-execute captcha after error:', e);
        }
      }
    }, 2000);
    toast({
      title: t('auth.verification_error'),
      description: t('auth.refresh_try_again'),
      variant: "destructive",
    });
  };

  // Use centralized session management
  const { ready, session, isAuthenticated } = useAppSession();

  // Use unified profile hook
  const { data: profile, isLoading: isLoadingProfile } = useProfile(session?.user?.id);

  // Check if Apple Sign In should be shown (iOS native only)
  useEffect(() => {
    const checkAppleSignIn = async () => {
      const detection = await detectNativeApp();
      const isIOSNative = detection.isNative && isIOS();
      const hasAppleSignIn = isAppleSignInAvailable();

      console.log('[Auth] Apple Sign In check:', {
        isIOSNative,
        hasAppleSignIn,
        shouldShow: isIOSNative && hasAppleSignIn,
      });

      setShowAppleSignIn(isIOSNative && hasAppleSignIn);
    };

    checkAppleSignIn();
  }, []);

  // Handle password recovery session - check if user has a recovery session
  useEffect(() => {
    const checkRecoverySession = async () => {
      // If we have a password reset link, wait for session to establish
      if (!isPasswordReset) {
        console.log("[Auth] Not a password reset link, skipping recovery check");
        return;
      }

      console.log("[Auth] Password reset link detected, waiting for session...", {
        ready,
        hasSession: !!session,
        hash: window.location.hash,
        search: window.location.search
      });

      // Wait for session to be ready
      if (ready !== 'ready') {
        console.log("[Auth] Session not ready yet, waiting...");
        return;
      }

      // Check if we're in native app
      const detection = await detectNativeApp();

      // Check if this is a password recovery session
      if (session) {
        console.log("[Auth] Session established - showing password reset form");
        console.log("[Auth] Session details:", {
          userId: session.user?.id,
          email: session.user?.email,
          role: session.user?.role
        });

        // Show password reset form when we have a session from an email link
        setView("reset-password");

        // Clean URL to remove any auth tokens/codes from being visible
        if (window.location.hash || window.location.search.includes('code=')) {
          window.history.replaceState({}, document.title, "/auth");
        }
      } else {
        console.log("[Auth] No session established yet");
        console.log("[Auth] Supabase should auto-detect session from URL with detectSessionInUrl=true");
        console.log("[Auth] If the code is in query params (PKCE), it can't be exchanged without code_verifier");
        console.log("[Auth] The implicit flow (hash-based tokens) should work automatically");

        // Show full-screen error message
        setShowPasswordResetError(true);

        // Clean the URL
        window.history.replaceState({}, document.title, "/auth");

        // Redirect after 10 seconds
        setTimeout(() => {
          setShowPasswordResetError(false);
          setView("signin");
        }, 10000);
      }
    };

    checkRecoverySession();
  }, [ready, session, isPasswordReset, toast, hasAuthCode, hasAccessToken]);

  // Handle navigation based on profile state - ONLY when session is ready
  useEffect(() => {
    // Don't redirect if we're in password reset mode
    if (view === "reset-password") {
      console.log("[Auth] In password reset mode, staying on page");
      return;
    }

    // Wait for session to be ready before making any routing decisions
    if (ready !== 'ready') {
      console.log("[Auth] Session not ready yet, waiting...");
      return;
    }

    // If not authenticated, stay on auth page
    if (!isAuthenticated) {
      console.log("[Auth] No authenticated session, showing auth page");
      return;
    }

    // If profile is still loading, wait BUT check if we should be in onboarding mode
    if (isLoadingProfile) {
      console.log("[Auth] Profile loading, waiting...");
      return;
    }

    // Check for pending Google OAuth flag (set before OAuth redirect)
    // This helps prevent redirects during OAuth flow for BOTH new signups AND existing users
    const hasPendingOAuth = sessionStorage.getItem('pending_google_oauth') === 'true';
    if (hasPendingOAuth && isAuthenticated) {
      console.log("[Auth] Detected pending Google OAuth - checking profile status");

      // If profile isn't loaded yet, stay put and show onboarding UI
      // This prevents Index from redirecting us back to /onboarding
      if (isLoadingProfile) {
        console.log("[Auth] Profile still loading during OAuth - staying on auth page");
        if (!isInOnboarding) {
          setIsInOnboarding(true);
        }
        return;
      }

      // Profile is loaded - check if username exists
      if (profile?.username) {
        // Existing user with username - clear flag and let them through
        console.log("[Auth] Existing user with username - clearing OAuth flag");
        sessionStorage.removeItem('pending_google_oauth');
        // Don't return - let normal navigation logic handle redirect
      } else {
        // New user needs username setup - stay in onboarding mode
        console.log("[Auth] New user needs username - staying in onboarding mode");
        if (!isInOnboarding) {
          setIsInOnboarding(true);
        }
        return;
      }
    }

    // CRITICAL: Check for null username FIRST before checking for existing username
    // This prevents race condition where cached username triggers redirect before we set onboarding flag
    if (isAuthenticated && profile && profile.username === null) {
      // User is authenticated but has no username (explicitly null) - show onboarding flow
      console.log("[Auth] No username found (null), setting onboarding mode");
      if (!isInOnboarding) {
        setIsInOnboarding(true);

        // Track signup based on provider (include A/B variant)
        const abVariant = getStoredLandingVariant();
        if (session?.user?.app_metadata?.provider === 'google') {
          trackOnboardingEvent({ eventType: "signup_google", metadata: { landing_variant: abVariant } });
        } else if (session?.user?.user_metadata?.apple_id) {
          // Apple users have apple_id in user_metadata
          trackOnboardingEvent({ eventType: "signup_apple", metadata: { landing_variant: abVariant } });
        }
      }
      // Don't navigate - we'll show the OnboardingFlow component below
      return; // Exit early to prevent any redirects
    }

    if (isAuthenticated && profile && !profile.username) {
      // Username is undefined (shouldn't happen, but handle it)
      console.log("[Auth] Username undefined, setting onboarding mode");
      if (!isInOnboarding) {
        setIsInOnboarding(true);
      }
      return; // Exit early
    }

    // Clean up - this should already be handled above, but just in case
    if (hasPendingOAuth && profile?.username && !isInOnboarding) {
      console.log("[Auth] Clearing pending_google_oauth flag - user authenticated with username");
      sessionStorage.removeItem('pending_google_oauth');
    }

    // Only redirect if we have a username AND not in onboarding mode
    if (profile?.username && !isInOnboarding) {
      console.log("[Auth] Username exists, navigating to dashboard:", profile.username);
      if (!localStorage.getItem("onboarding_complete")) {
        localStorage.setItem("onboarding_complete", "true");
      }
      safeNavigate(() => navigate("/", { replace: true }));
    }
  }, [ready, isAuthenticated, profile, isLoadingProfile, navigate, view, isInOnboarding, session]);

  // Handle GitHub OAuth callback
  useEffect(() => {
    const handleGitHubCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");

      if (code && state) {
        try {
          const stateData = JSON.parse(atob(state));

          // Get current user
          const {
            data: { user },
          } = await supabase.auth.getUser();

          if (user && stateData.userId === user.id) {
            setGoogleLoading(true);

            // Get current session for auth token
            const { data: sessionData } = await supabase.auth.getSession();
            if (!sessionData.session) {
              throw new Error("Not authenticated");
            }

            const { data, error } = await supabase.functions.invoke("github-oauth", {
              body: { code }, // Only send code, userId extracted from JWT
              headers: {
                Authorization: `Bearer ${sessionData.session.access_token}`
              }
            });

            if (error) throw error;

            if (data.success) {
              toast({
                title: t('auth.github_connected'),
                description: t('auth.gitfit_syncing'),
              });

              // Redirect back to the return URL
              navigate(stateData.returnUrl || "/", { replace: true });
            }
          }
        } catch (error: any) {
          console.error("GitHub OAuth callback error:", error);
          toast({
            title: t('auth.connection_failed'),
            description: t('auth.failed_connect_github'),
            variant: "destructive",
          });
          // Clean URL and stay on auth page
          window.history.replaceState({}, document.title, "/auth");
        } finally {
          setGoogleLoading(false);
        }
      }
    };

    handleGitHubCallback();
  }, [navigate, toast]);

  const handleGoogleSignIn = async () => {
    // Check age confirmation for signup
    if ((fromOnboarding || showSignUp || showEmailSection) && !ageConfirmed) {
      setAgeCheckboxGlow(true);
      setTimeout(() => setAgeCheckboxGlow(false), 2000);
      toast({
        title: t('auth.age_required_title'),
        description: t('auth.age_required_desc'),
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('[Auth] handleGoogleSignIn clicked - START');
      setGoogleLoading(true);

      // Proceed with Google sign-in (captcha already initiated on mount)
      await proceedWithGoogleSignIn();
      console.log('[Auth] proceedWithGoogleSignIn called - DONE');
    } catch (error) {
      console.error('[Auth] Error in handleGoogleSignIn:', error);
      captureException(error, {
        tags: { page: 'auth', action: 'google-sign-in' },
        extra: { fromOnboarding, showSignUp },
      });
      setGoogleLoading(false);
      toast({
        title: t('auth.error'),
        description: t('auth.failed_signin'),
        variant: "destructive",
      });
    }
  };

  const handleAppleSignIn = async () => {
    // Check age confirmation for signup
    if ((fromOnboarding || showSignUp || showEmailSection) && !ageConfirmed) {
      setAgeCheckboxGlow(true);
      setTimeout(() => setAgeCheckboxGlow(false), 2000);
      toast({
        title: t('auth.age_required_title'),
        description: t('auth.age_required_desc'),
        variant: "destructive",
      });
      return;
    }

    try {
      setAppleLoading(true);

      // Execute hCaptcha for bot protection
      console.log('[Auth] Executing hCaptcha for Apple Sign In...');
      if (captchaRef.current) {
        try {
          await captchaRef.current.execute();
        } catch (error) {
          console.error('[Auth] Error executing captcha:', error);
          throw new Error(t('auth.failed_security_verification'));
        }
      } else {
        throw new Error(t('auth.security_not_ready'));
      }

      // Wait for captcha token
      let captchaAttempts = 0;
      while (!captchaTokenRef.current && captchaAttempts < 300) {
        await new Promise(resolve => setTimeout(resolve, 100));
        captchaAttempts++;
      }

      if (!captchaTokenRef.current) {
        throw new Error(t('auth.captcha_timeout'));
      }

      console.log('[Auth] hCaptcha verified, proceeding with Apple Sign In');

      // Helper function to show toasts
      const showToast = (message: string, type: 'success' | 'error' | 'info') => {
        toast({
          title: message,
          variant: type === 'error' ? 'destructive' : undefined,
          duration: type === 'error' ? 5000 : 3000,
        });
      };

      // Call the Apple Sign In function with captcha token and toast callback
      const result = await signInWithApple(captchaTokenRef.current, showToast);

      // Reset captcha after use
      captchaTokenRef.current = null;
      setCaptchaToken(null);

      if (!result.success) {
        // Don't show error toast if user cancelled the dialog
        if (result.error === 'CANCELLED') {
          console.log('[Auth] Apple Sign-In cancelled by user');
        }
        return;
      }

      // Get user data
      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('[Auth] Error getting user:', userError);
        toast({
          title: t('auth.unexpected_error'),
          description: `Error: ${userError.message}`,
          variant: "destructive",
        });
        return;
      }

      if (!userData?.user) {
        console.error('[Auth] No user data returned');
        toast({
          title: t('auth.no_user_found'),
          description: t('auth.no_user_found'),
          variant: "destructive",
        });
        return;
      }

      console.log('[Auth] Apple Sign-In succeeded, user ID:', userData.user.id);

      // IMPORTANT: Aggressively clear ALL cached data from previous sessions
      // This prevents showing old cached data from a previously deleted account
      console.log('[Auth] Clearing ALL cached data (including profile) for fresh Apple Sign-In');
      queryClient.clear(); // Clear all React Query cache
      localStorage.removeItem('onboarding_complete'); // Clear onboarding flag
      sessionStorage.clear(); // Clear session storage
      console.log('[Auth] ✓ Cleared all cached data (React Query, localStorage, sessionStorage)');

      // Wait for profile to be created by database trigger
      // New accounts may take a moment for the trigger to create the profile
      let profile = null;
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms between attempts

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', userData.user.id)
          .maybeSingle();

        if (profileData) {
          profile = profileData;
          console.log('[Auth] Profile found:', { username: profile.username });
          break;
        }

        if (profileError && profileError.code !== 'PGRST116') {
          // PGRST116 is "no rows returned" - other errors are actual problems
          console.error('[Auth] Profile error:', profileError);
        }

        attempts++;
        console.log(`[Auth] Waiting for profile... attempt ${attempts}/${maxAttempts}`);
      }

      if (!profile) {
        console.error('[Auth] Profile not found after waiting');
        toast({
          title: t('auth.profile_setup_required'),
          description: t('auth.taking_to_profile'),
        });
        setIsFirstTimeUser(true);
        setIsInOnboarding(true);
        // Force profile refetch to ensure useEffect sees the updated state
        queryClient.invalidateQueries({ queryKey: ["profile", userData.user.id] });
        return;
      }

      // Check if user needs to complete onboarding (no username)
      if (profile.username === null || !profile.username) {
        console.log('[Auth] Apple Sign-In complete, but no username - entering onboarding mode');

        // Track Apple signup (new user)
        trackOnboardingEvent({ eventType: "signup_apple", metadata: { landing_variant: getStoredLandingVariant() } });

        setIsFirstTimeUser(true);
        setIsInOnboarding(true);
        // Force profile refetch to ensure useEffect sees the updated state
        queryClient.invalidateQueries({ queryKey: ["profile", userData.user.id] });
        // Don't navigate - let Auth.tsx's useEffect render OnboardingFlow
        return;
      }

      // User has username - redirect to dashboard
      console.log('[Auth] Apple Sign-In complete with username, redirecting to dashboard');
      setTimeout(() => {
        safeNavigate(() => navigate("/", { replace: true }));
      }, 500);
    } catch (error: any) {
      console.error('[Auth] Error in handleAppleSignIn:', error);
      toast({
        title: t('auth.unexpected_error'),
        description: `${error.message || error.toString()}`,
        variant: "destructive",
      });
    } finally {
      setAppleLoading(false);
    }
  };

  const proceedWithGoogleSignIn = async () => {
    try {
      console.log('[Auth] proceedWithGoogleSignIn - START');
      setShowGoogleConfirm(false);

      // Session state is managed by AppSessionProvider now
      console.log('[Auth] Checking current session...');

      // Check if we're in native iOS or Android app
      const detection = await detectNativeApp();
      const inNativeIOS = detection.isNative && isIOS();
      const inNativeAndroid = detection.isNative && isAndroid();
      const isWebBrowser = !detection.isNative;

      // Set flag to indicate user initiated Google OAuth (will be checked after redirect)
      // This prevents redirecting back to /onboarding during the OAuth flow
      // Applies to both new signups AND existing users signing in from /onboarding
      if (fromOnboarding || showSignUp) {
        console.log('[Auth] Setting pending_google_oauth flag for post-redirect detection');
        sessionStorage.setItem('pending_google_oauth', 'true');
      }

      // Always create a polling session as fallback
      // - Web browsers: Need polling
      // - Native iOS with Chrome: Need polling (Chrome doesn't trigger deep links)
      // - Native iOS with Safari: Will use deep link but polling won't hurt
      const sessionId = crypto.randomUUID();
      const { error: sessionError } = await supabase
        .from('oauth_sessions')
        .insert({
          session_id: sessionId,
          status: 'pending'
        });

      if (sessionError) {
        console.error('[Auth] Failed to create OAuth session:', sessionError);
        throw sessionError;
      }
      console.log('[Auth] Created OAuth polling session:', sessionId);

      // Get redirect URL - native apps also get session ID for Chrome fallback
      const oauthRedirectUrl = getOAuthRedirectUrl(sessionId);
      console.log('[Auth] OAuth redirect URL:', oauthRedirectUrl, 'Native iOS:', inNativeIOS, 'Native Android:', inNativeAndroid, 'Web Browser:', isWebBrowser);

      // Generate OAuth URL
      // Always skipBrowserRedirect so we get the URL to open manually
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: oauthRedirectUrl,
          skipBrowserRedirect: true, // Always skip so we can control where/how to open
        },
      });

      if (error) throw error;

      if (data?.url) {
        console.log('[Auth] OAuth URL from Supabase:', data.url);

        if (isWebBrowser) {
          // Check if we're on mobile Safari iOS (non-native web browser)
          // Mobile Safari doesn't handle window.open well - popups are blocked or broken
          const ua = navigator.userAgent;
          const isIOSDevice = /iPad|iPhone|iPod/.test(ua);
          const isSafariBrowser = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
          const isMobileSafari = isIOSDevice && isSafariBrowser;

          if (isMobileSafari) {
            // Mobile Safari: Use Supabase's normal redirect flow (not popup)
            // This avoids PKCE issues - the code_verifier stays in localStorage
            // and Supabase will auto-detect and exchange the code when we return to /auth
            console.log('[Auth] Mobile Safari detected - using standard redirect flow');
            
            // Don't use the polling redirect URL - redirect back to /auth directly
            // This keeps us on the same URL path where the PKCE code_verifier was stored
            // Include source=oauth so the password reset detection logic knows this is
            // an OAuth return, NOT a password reset PKCE code
            const origin = window.location.origin;
            const mobileRedirectUrl = `${origin}/auth?source=oauth`;
            
            // Re-initiate OAuth with the mobile-specific redirect URL
            // Let Supabase handle the redirect (don't skip)
            const { error: mobileError } = await supabase.auth.signInWithOAuth({
              provider: "google",
              options: {
                redirectTo: mobileRedirectUrl,
                // Don't skip browser redirect - let Supabase handle it normally
              },
            });
            
            if (mobileError) {
              console.error('[Auth] Mobile Safari OAuth error:', mobileError);
              throw mobileError;
            }
            // Supabase will redirect to Google, then back to /auth
            return;
          }

          // Desktop/other browsers: Open OAuth URL and start polling
          console.log('[Auth] Web browser - opening OAuth and starting polling');
          window.open(data.url, '_blank');

          toast({
            title: t('auth.complete_signin'),
            description: t('auth.new_window_opened'),
          });

          // Start polling for completion
          console.log('[Auth] Starting OAuth polling for session:', sessionId);
          let pollAttempts = 0;
          const maxPollAttempts = 150; // 5 minutes at 2 second intervals
          const pollInterval = setInterval(async () => {
            pollAttempts++;
            console.log(`[Auth] Polling attempt ${pollAttempts}/${maxPollAttempts} for session:`, sessionId);

            const { data: session, error: pollError } = await supabase
              .from('oauth_sessions')
              .select('status, auth_code')
              .eq('session_id', sessionId)
              .single();

            if (pollError) {
              console.error('[Auth] Polling error:', pollError);
              // Don't fail on "not found" errors during first few attempts
              // The record might not exist yet if auth flow is still starting
              if (pollError.code === 'PGRST116' && pollAttempts < 5) {
                console.log('[Auth] Session not found yet, continuing to poll...');
                return;
              }
              clearInterval(pollInterval);
              toast({
                title: t('auth.auth_error'),
                description: t('auth.failed_complete_signin'),
                variant: "destructive",
              });
              return;
            }

            console.log('[Auth] Polling result:', { status: session?.status, hasCode: !!session?.auth_code });

            if (session?.status === 'completed' && session.auth_code) {
              console.log('[Auth] OAuth completed! Exchanging code...');
              clearInterval(pollInterval);

              // Exchange the auth code for a session
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(session.auth_code);

              if (exchangeError) {
                console.error('[Auth] Code exchange error:', exchangeError);
                toast({
                  title: t('auth.auth_error'),
                  description: exchangeError.message,
                  variant: "destructive",
                });
              } else {
                console.log('[Auth] Successfully authenticated!');
                toast({
                  title: t('auth.welcome.greeting'),
                  description: t('auth.google_signin_success'),
                });
                // Supabase will trigger onAuthStateChange and handle navigation
              }

              // Cleanup the session
              await supabase.from('oauth_sessions').delete().eq('session_id', sessionId);
            }
          }, 2000); // Poll every 2 seconds

          // Stop polling after 5 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            console.log('[Auth] OAuth polling timeout');
          }, 5 * 60 * 1000);
        } else if (inNativeIOS || inNativeAndroid) {
          // Native iOS/Android: Open in external browser
          // - Safari/Chrome: Deep link will work, polling is backup
          // - Other browsers: Deep link may fail, polling catches it
          console.log('[Auth] Native app - opening OAuth in external browser');
          console.log('[Auth] Detection results:', { inNativeIOS, inNativeAndroid, isWebBrowser, sessionId });
          console.log('[Auth] OAuth URL:', data.url);

          const result = await openInExternalBrowser(data.url);
          console.log('[Auth] openInExternalBrowser result:', result);

          if (!result.success) {
            toast({
              title: t('auth.browser_open_error'),
              description: result.error || t('auth.browser_open_fail'),
              variant: "destructive",
            });
            return;
          }

          const waitingToast = toast({
            title: t('auth.waiting_signin'),
            description: t('auth.complete_browser_signin'),
            duration: Infinity, // Keep showing until dismissed
          });

          // Start polling as fallback (needed for Chrome, harmless for Safari)
          console.log('[Auth] Starting OAuth polling for session:', sessionId);
          let pollAttempts = 0;
          const maxPollAttempts = 150; // 5 minutes at 2 second intervals
          const pollInterval = setInterval(async () => {
            pollAttempts++;
            console.log(`[Auth] Native app polling attempt ${pollAttempts}/${maxPollAttempts} for session:`, sessionId);

            const { data: session, error: pollError } = await supabase
              .from('oauth_sessions')
              .select('status, auth_code')
              .eq('session_id', sessionId)
              .single();

            if (pollError) {
              console.error('[Auth] Polling error:', pollError);
              // Don't fail on "not found" errors during first few attempts
              if (pollError.code === 'PGRST116' && pollAttempts < 5) {
                console.log('[Auth] Session not found yet, continuing to poll...');
                return;
              }
              // For other errors or persistent not found, just continue polling silently
              // User might have closed browser, will timeout naturally
              return;
            }

            console.log('[Auth] Native app polling result:', { status: session?.status, hasCode: !!session?.auth_code });

            if (session?.status === 'completed' && session.auth_code) {
              console.log('[Auth] OAuth completed via polling! Exchanging code...');
              clearInterval(pollInterval);
              waitingToast.dismiss();

              // Exchange the auth code for a session
              const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(session.auth_code);

              if (exchangeError) {
                console.error('[Auth] Code exchange error:', exchangeError);
                toast({
                  title: t('auth.auth_error'),
                  description: exchangeError.message,
                  variant: "destructive",
                });
              } else {
                console.log('[Auth] Successfully authenticated via polling!');
                toast({
                  title: t('auth.welcome.greeting'),
                  description: t('auth.google_signin_success'),
                });
              }

              // Cleanup the session
              await supabase.from('oauth_sessions').delete().eq('session_id', sessionId);
            }
          }, 2000); // Poll every 2 seconds

          // Stop polling after 5 minutes
          setTimeout(() => {
            clearInterval(pollInterval);
            waitingToast.dismiss();
            console.log('[Auth] Native app OAuth polling timeout - no response after 5 minutes');
            toast({
              title: t('auth.signin_timeout'),
              description: t('auth.browser_signin_timeout'),
              variant: "destructive",
            });
          }, 5 * 60 * 1000);
        } else {
          // Fallback: Manually redirect in same window
          console.log('[Auth] Redirecting to OAuth URL in same window');
          window.location.href = data.url;
        }
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      toast({
        title: t('auth.error'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSendPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setEmailLoading(true);

      console.log('[Auth] Password reset initiated, captcha token state:', {
        hasToken: !!captchaTokenRef.current,
        captchaInitiated,
      });

      // Always reset and get fresh captcha token
      if (captchaRef.current?.resetCaptcha) {
        console.log('[Auth] Resetting captcha for fresh token');
        captchaRef.current.resetCaptcha();
        captchaTokenRef.current = null;
        setCaptchaToken(null);
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      // If captcha not initiated yet, initiate it now
      if (!captchaInitiated) {
        console.log('[Auth] Initiating captcha');
        setCaptchaInitiated(true);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Execute captcha to get fresh token
      if (captchaRef.current?.execute) {
        try {
          console.log('[Auth] Executing captcha for password reset');
          await captchaRef.current.execute();
        } catch (error) {
          console.error('[Auth] Error executing captcha:', error);
          throw new Error(t('auth.failed_security_verification'));
        }
      } else {
        throw new Error(t('auth.security_not_ready'));
      }

      // Wait for captcha token with timeout
      console.log('[Auth] Waiting for captcha verification...');
      let attempts = 0;
      while (!captchaTokenRef.current && attempts < 300) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!captchaTokenRef.current) {
        throw new Error(t('auth.captcha_timeout'));
      }

      // For password reset, always use web URL (not native app URL)
      // This is because Supabase uses PKCE flow for email links by default,
      // which doesn't work with universal links (code_verifier is lost)
      // By sending to web, the PKCE flow completes properly in the browser
      const passwordResetUrl = `${getPasswordResetRedirectUrl()}/auth`;

      console.log('[Auth] Password reset redirect URL:', passwordResetUrl);

      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: passwordResetUrl,
        captchaToken: captchaTokenRef.current,
      });

      console.log('[Auth] Password reset response:', { data, error });

      if (error) {
        console.error('[Auth] Password reset error:', error);
        throw error;
      }

      setEmailSent(true);

      toast({
        title: "Success",
        description: "Check your email for a password reset link. If you don't receive it within a few minutes, check your Supabase email settings.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot - if bot detected, return fake success
    if (isSignInBot) {
      setPasswordLoading(true);
      await getFakeBotResponse(FAKE_AUTH_SUCCESS);
      toast({
        title: "Success",
        description: "Signed in successfully!",
      });
      setPasswordLoading(false);
      return;
    }

    // Validate Gmail users to use Google OAuth
    if (passwordEmail.toLowerCase().endsWith('@gmail.com')) {
      toast({
        title: "Please Use Google Sign-In",
        description: "Gmail users must use 'Continue With Google' to sign in.",
        variant: "destructive",
      });
      return;
    }

    try {
      setPasswordLoading(true);

      console.log('[Auth] Sign-in initiated, current captcha token state:', {
        hasToken: !!captchaTokenRef.current,
        tokenLength: captchaTokenRef.current?.length,
        captchaInitiated,
        hasRef: !!captchaRef.current,
      });

      // Always reset and get fresh captcha token for sign-in
      // This ensures we don't use an expired or stale token
      if (captchaRef.current?.resetCaptcha) {
        console.log('[Auth] Resetting captcha for fresh token');
        captchaRef.current.resetCaptcha();
        captchaTokenRef.current = null;
        setCaptchaToken(null);
      }

      // Small delay after reset
      await new Promise(resolve => setTimeout(resolve, 100));

      // If captcha not initiated yet, initiate it now
      if (!captchaInitiated) {
        console.log('[Auth] Initiating captcha');
        setCaptchaInitiated(true);
        // Give time for captcha to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Execute captcha to get fresh token
      if (captchaRef.current?.execute) {
        try {
          console.log('[Auth] Executing captcha for fresh token');
          await captchaRef.current.execute();
        } catch (error) {
          console.error('[Auth] Error executing captcha:', error);
          throw new Error(t('auth.failed_security_verification'));
        }
      } else {
        throw new Error(t('auth.security_not_ready'));
      }

      // Wait for captcha token with timeout
      console.log('[Auth] Waiting for captcha verification...');
      let attempts = 0;
      while (!captchaTokenRef.current && attempts < 300) { // 30 seconds max
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!captchaTokenRef.current) {
        throw new Error(t('auth.captcha_timeout'));
      }

      console.log('[Auth] Captcha verified, signing in with token');

      // Session state is managed by AppSessionProvider now
      const { error } = await supabase.auth.signInWithPassword({
        email: passwordEmail,
        password,
        options: {
          captchaToken: captchaTokenRef.current,
        },
      });

      if (error) throw error;

      // Reset captcha after successful sign-in
      if (captchaRef.current?.resetCaptcha) {
        captchaRef.current.resetCaptcha();
      }
      captchaTokenRef.current = null;
      setCaptchaToken(null);

      toast({
        title: "Success",
        description: "Signed in successfully!",
      });
    } catch (error: any) {
      // Reset captcha on error
      if (captchaRef.current?.resetCaptcha) {
        captchaRef.current.resetCaptcha();
      }
      captchaTokenRef.current = null;
      setCaptchaToken(null);

      captureException(error, {
        tags: { page: 'auth', action: 'password-sign-in' },
        extra: { email: passwordEmail, hasCaptcha: !!captchaTokenRef.current },
      });

      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    try {
      setResetPasswordLoading(true);

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Password updated successfully! You can now sign in.",
      });

      // Clear URL parameters and redirect to sign in
      window.history.replaceState({}, document.title, "/auth");
      setView("signin");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error: any) {
      captureException(error, {
        tags: { page: 'auth', action: 'reset-password' },
      });
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handlePasswordSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check honeypot - if bot detected, return fake success
    if (isSignUpBot) {
      setPasswordLoading(true);
      await getFakeBotResponse(FAKE_AUTH_SUCCESS);
      toast({
        title: t('auth.success'),
        description: t('auth.account_created_verify'),
      });
      setPasswordLoading(false);
      return;
    }

    // Validate age confirmation
    if (!ageConfirmed) {
      setAgeCheckboxGlow(true);
      setTimeout(() => setAgeCheckboxGlow(false), 2000);
      toast({
        title: "Age Confirmation Required",
        description: "You must confirm that you are at least 13 years old to create an account.",
        variant: "destructive",
      });
      return;
    }

    // Validate Gmail users to use Google OAuth
    if (passwordEmail.toLowerCase().endsWith('@gmail.com')) {
      toast({
        title: "Please Use Google Sign-In",
        description: "Gmail users must use 'Continue With Google' to sign up.",
        variant: "destructive",
      });
      return;
    }

    // Block disposable/temporary email addresses
    if (isDisposableEmail(passwordEmail)) {
      toast({
        title: "Invalid Email Address",
        description: getDisposableEmailErrorMessage(),
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters long",
        variant: "destructive",
      });
      return;
    }

    try {
      setPasswordLoading(true);

      // Execute captcha if we don't have a token yet
      if (!captchaTokenRef.current) {
        console.log('[Auth] No captcha token, executing captcha...');

        // Check if captcha ref is available and has required methods
        if (captchaRef.current &&
          typeof captchaRef.current.execute === 'function' &&
          typeof captchaRef.current.resetCaptcha === 'function') {
          try {
            // Reset and execute captcha
            captchaRef.current.resetCaptcha();
            await new Promise(resolve => setTimeout(resolve, 100)); // Small delay after reset
            captchaRef.current.execute();

            // Show user-friendly message while waiting
            toast({
              title: "Verifying...",
              description: "Completing security check",
            });

            // Wait up to 30 seconds for captcha to complete
            let attempts = 0;
            while (!captchaTokenRef.current && attempts < 300) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }

            if (!captchaTokenRef.current) {
              console.log('[Auth] Captcha verification timeout');
              setPasswordLoading(false);
              toast({
                title: "Security Check Timeout",
                description: "Please refresh the page and try again.",
                variant: "destructive",
              });
              return;
            }
          } catch (error) {
            console.error('[Auth] Error executing captcha:', error);
            setPasswordLoading(false);
            toast({
              title: "Verification Error",
              description: "Please refresh the page and try again.",
              variant: "destructive",
            });
            return;
          }
        } else {
          console.error('[Auth] Captcha ref not ready or missing methods');
          setPasswordLoading(false);
          toast({
            title: "Security Check Not Ready",
            description: "Please refresh the page and try again.",
            variant: "destructive",
          });
          return;
        }
      }

      // Redirect to email verified page after verification
      const baseUrl = window.location.origin;
      const verifiedUrl = `${baseUrl}/email-verified`;

      const { data, error } = await supabase.auth.signUp({
        email: passwordEmail,
        password,
        options: {
          emailRedirectTo: verifiedUrl,
          captchaToken: captchaTokenRef.current,
        },
      });

      // Reset captcha after use
      captchaRef.current?.resetCaptcha();
      captchaTokenRef.current = null;
      setCaptchaToken(null);

      if (error) throw error;

      // Check if user already exists (Supabase returns data but with identities = [])
      if (data.user && data.user.identities && data.user.identities.length === 0) {
        toast({
          title: "Account Already Exists",
          description: "This email is already registered. Please sign in instead or try signing in with Google if you used that method.",
          variant: "destructive",
        });
        // Switch to sign in view
        setShowSignUp(false);
        return;
      }

      // Save pre-auth metrics and track IP after successful signup (non-blocking)
      if (data.user) {
        // Track email signup
        trackOnboardingEvent({ eventType: "signup_email", metadata: { landing_variant: getStoredLandingVariant() } });

        // Save pre-auth metrics
        const preAuthMetrics = loadPreAuthMetrics();
        if (preAuthMetrics.length > 0) {
          console.log(`[Auth] Saving ${preAuthMetrics.length} pre-auth metrics to backend after signup`);
          savePreAuthMetricsToBackend(passwordEmail, preAuthMetrics).catch(err => {
            console.error("Failed to save pre-auth metrics:", err);
          });
        }

        // Track signup IP
        const trackSignupIP = async () => {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            await supabase.functions.invoke("track-signup-ip", {
              headers: {
                Authorization: `Bearer ${sessionData.session?.access_token}`,
              },
            });
          } catch (err) {
            console.error("Failed to track signup IP:", err);
          }
        };
        trackSignupIP();
      }

      toast({
        title: t('auth.success'),
        description: t('auth.account_created_verify'),
      });

      // Switch to sign-in mode, preserving the password
      setShowSignUp(false);
      // Password is already in state, confirmPassword will be cleared
      setConfirmPassword("");
    } catch (error: any) {
      // Handle specific error cases
      const errorMessage = error.message.toLowerCase();

      captureException(error, {
        tags: { page: 'auth', action: 'password-sign-up' },
        extra: {
          email: passwordEmail,
          hasCaptcha: !!captchaTokenRef.current,
          isExistingUser: errorMessage.includes("already registered") ||
            errorMessage.includes("user already exists") ||
            errorMessage.includes("email already in use")
        },
      });

      if (errorMessage.includes("already registered") ||
        errorMessage.includes("user already exists") ||
        errorMessage.includes("email already in use")) {
        toast({
          title: "Account Already Exists",
          description: "This email is already registered. Please sign in instead or try signing in with Google if you used that method.",
          variant: "destructive",
        });
        // Switch to sign in view
        setShowSignUp(false);
      } else {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  // Show full-screen password reset error
  if (showPasswordResetError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-destructive"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">{t('ui.text.unable_to_process_reset_link')}</h1>
            <p className="text-muted-foreground text-lg">{t('ui.text.please_try_again_and_make_sure_you_follow_the_link')}</p>
          </div>
          <div className="text-sm text-muted-foreground">{t('ui.text.redirecting_in_a_moment')}</div>
        </div>
      </div>
    );
  }

  // Show loading while session is being checked
  if (ready !== 'ready') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <GitFitGridLoader className="mx-auto" />
          <p className="mt-4 text-muted-foreground">{t('ui.text.loading')}</p>
        </div>
      </div>
    );
  }

  // Show onboarding flow if user is authenticated but has no username OR if in onboarding
  if (isAuthenticated && profile && (profile.username === null || isInOnboarding)) {
    console.log('[Auth] Rendering OnboardingFlow - username:', profile.username, 'isInOnboarding:', isInOnboarding);
    return (
      <OnboardingFlow
        userId={session!.user.id}
      />
    );
  }

  // Show password reset form
  if (view === "reset-password") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6 p-8 bg-card rounded-lg border">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold">{t('auth.reset_password_title')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.enter_new_password')}</p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('auth.new_password_label')}</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">{t('auth.confirm_new_password_label')}</Label>
              <div className="relative">
                <Input
                  id="confirm-new-password"
                  type={showConfirmNewPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={resetPasswordLoading}>
              {resetPasswordLoading ? t('auth.updating_password') : t('auth.update_password')}
            </Button>
          </form>

          <div className="text-center">
            <button
              onClick={() => {
                window.history.replaceState({}, document.title, "/auth");
                setView("signin");
              }}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              {t('auth.back_to_sign_in')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show landing page
  if (view === "landing") {
    return <AuthLanding onGetStarted={() => setView("signin")} onSignIn={() => setView("signin")} />;
  }

  // Generate fake contribution data for preview
  const generateFakeData = () => {
    return Array.from({ length: 52 }, (_, week) =>
      Array.from({ length: 7 }, (_, day) => {
        const random = Math.random();
        return random > 0.3 ? Math.floor(random * 4) + 1 : 0;
      })
    );
  };

  const contributionData = generateFakeData();

  // Show sign in form
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-background p-4 max-[375px]:p-3"
      style={{
        transform: isAndroidDevice && isInputFocused ? 'translateY(-30vh)' : 'translateY(0)',
        transition: 'transform 0.3s ease-out'
      }}
      onPointerDown={(e) => {
        // On Android, tapping outside inputs should blur them to reset the transform
        if (isAndroidDevice && isInputFocused) {
          const target = e.target as HTMLElement;
          if (target.tagName !== 'INPUT' && target.tagName !== 'BUTTON' && target.tagName !== 'A') {
            (document.activeElement as HTMLElement)?.blur();
          }
        }
      }}
    >
      <div className="w-full max-w-md space-y-6 max-[375px]:space-y-4 p-8 max-[375px]:p-5 bg-card rounded-lg border">
        <div className="text-center space-y-3 max-[375px]:space-y-2">
          {/* Contribution Chart Preview - Above Logo */}
          <div className="relative opacity-40 flex justify-center">
            <div className="flex gap-0.5">
              {contributionData.slice(0, 12).map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-0.5">
                  {week.slice(0, 3).map((intensity, dayIndex) => {
                    const colors = {
                      0: "hsl(var(--muted))",
                      1: "hsl(142 30% 85%)",
                      2: "hsl(142 40% 65%)",
                      3: "hsl(142 50% 45%)",
                      4: "hsl(142 60% 35%)",
                    };
                    return (
                      <div
                        key={dayIndex}
                        className="w-2 h-2 rounded-sm"
                        style={{ backgroundColor: colors[intensity as keyof typeof colors] }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <h1 className="text-3xl max-[375px]:text-2xl font-bold tracking-tight">gitfit<span className="font-extralight opacity-60">.ai</span></h1>
          <p className="text-sm max-[375px]:text-xs text-muted-foreground">
            {(fromOnboarding || showSignUp)
              ? (searchParams.get("from") === "onboarding" ? t('auth.subtitle_save_progress') : t('auth.subtitle_signup_free'))
              : t('auth.subtitle_signin_track')}
          </p>

          {(fromOnboarding || showSignUp) && (
            <div className="flex justify-center gap-4 text-xs max-[375px]:text-[11px] text-muted-foreground/50 pt-1">
              <span>{t('auth.trust_no_card')}</span>
              <span>{t('auth.trust_private_data')}</span>
            </div>
          )}
        </div>

        <div className="space-y-3 max-[375px]:space-y-2.5">
          {/* Age Verification - Required for signup only */}
          {(fromOnboarding || showSignUp || showEmailSection) && (
            <div
              className={`flex items-center space-x-2 p-3 max-[375px]:p-2.5 bg-muted/50 rounded-lg border cursor-pointer hover:bg-muted/70 transition-all ${ageCheckboxGlow ? 'animate-pulse ring-2 ring-primary/50 shadow-lg shadow-primary/20' : ''
                }`}
              onClick={() => setAgeConfirmed(!ageConfirmed)}
            >
              <Checkbox
                id="age-confirm-main"
                checked={ageConfirmed}
                onCheckedChange={(checked) => setAgeConfirmed(checked === true)}
                className="pointer-events-none"
              />
              <span className="text-xs text-muted-foreground select-none flex-1 pointer-events-none">
                {t('auth.confirm_age')}
              </span>
            </div>
          )}

          {/* Social Sign-In Options */}
          <AnimatePresence mode="wait">
            {!showEmailSection ? (
              <motion.div
                key="social-buttons"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-3 max-[375px]:space-y-2.5"
              >
                {/* Apple Sign In - Only show on iOS native */}
                {showAppleSignIn && (
                  <Button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={appleLoading}
                    variant="outline"
                    className="w-full h-12 text-base max-[375px]:h-11"
                    size="lg"
                  >
                    {appleLoading ? (
                      <MiniGridLoader className="w-5 h-5" />
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        {t('auth.continue_apple')}
                      </>
                    )}
                  </Button>
                )}

                <Button type="button" onClick={handleGoogleSignIn} disabled={googleLoading} variant="outline" className="w-full h-12 text-base max-[375px]:h-11" size="lg">
                  {googleLoading ? (
                    <MiniGridLoader className="w-5 h-5" />
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="currentColor"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="currentColor"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      {t('auth.continue_google')}
                    </>
                  )}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="back-button"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  type="button"
                  onClick={() => setShowEmailSection(false)}
                  variant="ghost"
                  className="w-full h-10 text-sm max-[375px]:h-9"
                  size="sm"
                >
                  {t('auth.back_social')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Invisible hCaptcha - Only render when needed */}
          {captchaInitiated && (
            <SafeHCaptcha
              ref={captchaRef}
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={handleCaptchaVerify}
              onExpire={handleCaptchaExpire}
              onLoad={handleCaptchaLoad}
              onError={handleCaptchaError}
              size="invisible"
              loadAsync={true}
            />
          )}

          {!showEmailSection ? (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs max-[375px]:text-[11px] uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={() => setShowEmailSection(true)}
                variant="outline"
                className="w-full h-12 text-base max-[375px]:h-11"
                size="lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                {t('auth.continue_email')}
              </Button>

              {/* App Store buttons - mobile web only */}
              {isMobileWeb && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs max-[375px]:text-[11px] uppercase">
                      <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                    </div>
                  </div>

                  <div className="flex flex-row items-center justify-center gap-3">
                    <a
                      href="https://apps.apple.com/us/app/gitfit-ai/id6754495386"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackStoreClick("apple", "auth")}
                      className="cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 active:scale-95"
                    >
                      <img
                        src="/Download_AppStore.png"
                        alt={t('showcase.download_app_store')}
                        className="h-11 w-auto"
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=ai.gitfit.app&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackStoreClick("google", "auth")}
                      className="cursor-pointer rounded-xl transition-all duration-200 ease-out hover:scale-105 active:scale-95"
                    >
                      <img
                        src="/Download_PlayStore.png"
                        alt={t('showcase.download_play_store')}
                        className="h-11 w-auto"
                      />
                    </a>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="relative flex justify-center text-xs max-[375px]:text-[11px] uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              {/* Username/Password Form */}
              {!showSignUp ? (
                <form onSubmit={handlePasswordSignIn} className="space-y-3 max-[375px]:space-y-2.5">
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <input {...signInHoneypotProps} />

                  <div className="space-y-1.5">
                    <Label htmlFor="password-email" className="text-sm">{t('auth.email_label')}</Label>
                    <Input
                      id="password-email"
                      type="email"
                      placeholder={t('forms.placeholders.you_example_com')}
                      value={passwordEmail}
                      onChange={(e) => setPasswordEmail(e.target.value)}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      required
                      className="h-11 text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-sm">{t('auth.password_label')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        required
                        className="h-11 text-base pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 text-base max-[375px]:h-11" disabled={passwordLoading}>
                    {passwordLoading ? <MiniGridLoader className="w-5 h-5" /> : t('auth.sign_in')}
                  </Button>

                  {/* Subtle links below sign-in button */}
                  <div className="flex justify-center gap-1 text-sm max-[375px]:text-xs text-muted-foreground pt-1">
                    <span>{t('auth.dont_have_account')}</span>
                    <button
                      type="button"
                      onClick={() => setShowSignUp(true)}
                      className="text-primary hover:underline font-medium"
                    >
                      {t('auth.sign_up')}
                    </button>
                  </div>
                  <div className="flex justify-center gap-1 text-sm max-[375px]:text-xs text-muted-foreground">
                    <span>{t('auth.forgot_password')}</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!passwordEmail) {
                          toast({
                            title: t('auth.email_required'),
                            description: t('auth.enter_email_first'),
                            variant: "destructive",
                          });
                          return;
                        }
                        try {
                          setEmailLoading(true);

                          // Always reset and get fresh captcha token
                          if (captchaRef.current?.resetCaptcha) {
                            captchaRef.current.resetCaptcha();
                            captchaTokenRef.current = null;
                            setCaptchaToken(null);
                          }

                          await new Promise(resolve => setTimeout(resolve, 100));

                          // If captcha not initiated yet, initiate it now
                          if (!captchaInitiated) {
                            setCaptchaInitiated(true);
                            await new Promise(resolve => setTimeout(resolve, 500));
                          }

                          // Execute captcha to get fresh token
                          if (captchaRef.current?.execute) {
                            await captchaRef.current.execute();
                          } else {
                            throw new Error('Security verification not ready. Please refresh the page.');
                          }

                          // Wait for captcha token
                          let attempts = 0;
                          while (!captchaTokenRef.current && attempts < 300) {
                            await new Promise(resolve => setTimeout(resolve, 100));
                            attempts++;
                          }

                          if (!captchaTokenRef.current) {
                            throw new Error('Captcha verification timed out. Please try again.');
                          }

                          // For password reset, always use web URL (not native app URL)
                          const passwordResetUrl = `${getPasswordResetRedirectUrl()}/auth`;

                          const { error } = await supabase.auth.resetPasswordForEmail(
                            passwordEmail,
                            {
                              redirectTo: passwordResetUrl,
                              captchaToken: captchaTokenRef.current,
                            }
                          );

                          if (error) throw error;

                          toast({
                            title: "Success",
                            description: "Check your email for the password reset link",
                          });
                        } catch (error: any) {
                          toast({
                            title: "Error",
                            description: error.message,
                            variant: "destructive",
                          });
                        } finally {
                          setEmailLoading(false);
                        }
                      }}
                      className="text-primary hover:underline font-medium"
                      disabled={emailLoading}
                    >
                      {emailLoading ? t('auth.sending') : t('auth.send_reset_link')}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handlePasswordSignUp} className="space-y-3 max-[375px]:space-y-2.5">
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <input {...signUpHoneypotProps} />

                  <div className="space-y-1.5">
                    <Label htmlFor="signup-email" className="text-sm">{t('auth.email_label')}</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder={t('forms.placeholders.you_example_com')}
                      value={passwordEmail}
                      onChange={(e) => setPasswordEmail(e.target.value)}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() => setIsInputFocused(false)}
                      required
                      className="h-11 text-base"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-password" className="text-sm">{t('auth.password_label')}</Label>
                    <div className="relative">
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        required
                        minLength={6}
                        className="h-11 text-base pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-sm">{t('auth.confirm_password_label')}</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setIsInputFocused(false)}
                        required
                        minLength={6}
                        className="h-11 text-base pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 text-base max-[375px]:h-11" disabled={passwordLoading || !ageConfirmed}>
                    {passwordLoading ? <MiniGridLoader className="w-5 h-5" /> : t('auth.create_account')}
                  </Button>

                  {/* Link to go back to sign in */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="flex justify-center gap-1 text-sm max-[375px]:text-xs text-muted-foreground pt-1"
                  >
                    <span>{t('auth.already_have_account')}</span>
                    <button
                      type="button"
                      onClick={() => setShowSignUp(false)}
                      className="text-primary hover:underline font-medium"
                    >
                      {t('auth.sign_in')}
                    </button>
                  </motion.div>
                </form>
              )}
            </>
          )}

          {!fromOnboarding && !showSignUp && (
            <Button variant="ghost" className="w-full text-sm" onClick={() => setView("landing")}>
              {t('auth.back')}
            </Button>
          )}
        </div>
      </div>

      {/* Terms and Privacy Policy - Bottom center of page */}
      <div className="fixed bottom-[26px] left-0 right-0 flex justify-center px-4">
        <p className="text-[10px] text-muted-foreground/40 text-center max-w-md">
          {t('auth.terms_prefix')}{" "}
          <Link to="/terms-and-conditions" className="underline hover:text-muted-foreground/60">
            {t('auth.terms_conditions')}
          </Link>{" "}
          {t('auth.and')}{" "}
          <Link to="/privacy-policy" className="underline hover:text-muted-foreground/60">
            {t('auth.privacy_policy')}
          </Link>
          {t('auth.terms_suffix')}
        </p>
      </div>

      {/* Google OAuth Confirmation Dialog - Removed to allow direct sign-in */}
      {/* <AlertDialog open={showGoogleConfirm} onOpenChange={setShowGoogleConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-6 w-6 text-green-600" />
              <AlertDialogTitle>{t('ui.text.secure_authentication')}</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-2 text-left">
              <p>{t('ui.text.you_ll_be_redirected_to_authenticate_with_google_i')}</p>
              <p className="text-sm text-muted-foreground">{t('ui.text.the_url_will_contain_supabase_co_this_is_our_secur')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={proceedWithGoogleSignIn}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> */}
    </div>
  );
};

export default Auth;
